import { useLocation } from "react-router-dom";
import { useCallback, useMemo, useState } from "react";
import { Formik, Field, Form } from "formik";
import { useDispatch, useSelector } from "react-redux";
import get from "lodash/get";
import { v4 as uuid } from "uuid";
import * as Yup from "yup";
import classNames from "classnames";
import {
  BUTTON_TEXT,
  REGEX,
  EMPTY_STRING,
  EMPTY_ARRAY,
  INDEX,
  ALL_SERVICE_TYPE_OPTIONS,
  EMPTY_OBJECT,
} from "../../../../constants";
import { actions as alertActions } from "../../Alert/alert.reducers";
import {
  selectSelectedDataModelId,
  selectServices,
  createService,
} from "../../../../pages/DataModels";
import Spinner, { SPINNER_NAMES } from "../../Spinner";
import Modal, { setSelectedModal } from "../../Modal";
import { selectNodes } from "../../../../pages/Applications";
import { ROOT_NODE } from "../../../../pages/DataModels/datamodels.constants";
import { VALIDATION_MESSAGES } from "../../../../constants";
import { actions as dataModalActions } from "../../../../pages/DataModels/dataModels.reducers";

const id = uuid();

const { SUBMIT } = BUTTON_TEXT;
const { setAlert } = alertActions;
const { CREATE_SERVICE } = SPINNER_NAMES;
const {
  SPECIAL_CHARACTERS_NUMBERS_WHITESPACES_AT_START,
  SPECIAL_CHARACTERS,
  SPECIAL_CHARACTERS_WHITESPACES_AT_END,
  WHITESPACES_AT_START,
} = REGEX;
const { FIRST } = INDEX;
const ENDPOINT = "endpoint";
const SUBFLOW = "subflow";
const {
  NAME_SHOULD_CONTAIN,
  NAME_SHOULD_NOT_CONTAIN,
  MIN_CHAR_LENGTH,
  MAX_CHAR_LENGTH_TWENTY_ONE,
  NAME_SHOULD_NOT_CONTAIN_AT_END,
  SERVICE_REQUIRED,
  CHECK_EXSISTANCE,
  SERVICE_EXISTS,
  DESCRIPTION_SHOULD_NOT_CONTAIN,
} = VALIDATION_MESSAGES;
const { setServiceFilters , setNewService} = dataModalActions;

const ManageServiceModal = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const dataModelId = useSelector(selectSelectedDataModelId);
  const allServices = useSelector(selectServices);
  const selectedServiceNodes = useSelector(selectNodes);

  const [isSpinnerActive, setIsSpinnerActive] = useState(false);

  const serviceTypeOptions = useMemo(() => {
    const currentTab = location.pathname.split("/").pop();
    return ALL_SERVICE_TYPE_OPTIONS[currentTab];
  }, [location.pathname]);
  
  const initialValues = useMemo(
    () => ({
      name: EMPTY_STRING,
      description: EMPTY_STRING,
      serviceType: serviceTypeOptions[FIRST].value,
    }),
    [serviceTypeOptions]
  );

  const servicesNames = allServices.map(({ name }) => name.toLowerCase());

  const handleClose = useCallback(() => {
    dispatch(setSelectedModal(null));
  }, [dispatch]);

  const handleOnSubmit = useCallback(
    (values) => {
      const { name, description, serviceType } = values;
      const isExistingService = allServices.some(
        (service) => service.name === name
      );
      if (isExistingService) {
        dispatch(
          setAlert({
            message: SERVICE_EXISTS,
          })
        );
      } else {
        const endpointStartNode = get(
          selectedServiceNodes,
          ["0", "nodes", "0", "card"],
          EMPTY_OBJECT
        );
        const backgroundTaskStartNode = get(
          selectedServiceNodes,
          ["0", "nodes", "1", "card"],
          EMPTY_OBJECT
        );
        const subflowStartNode = get(
          selectedServiceNodes,
          ["0", "nodes", "2", "card"],
          EMPTY_OBJECT
        );
        const startNode =
          serviceType === ENDPOINT
            ? endpointStartNode
            : serviceType === SUBFLOW
            ? subflowStartNode
            : backgroundTaskStartNode;
        const flow = {
          nodes: [
            {
              ...ROOT_NODE,
              data: { ...startNode, id },
              id,
            },
          ],
          edges: EMPTY_ARRAY,
        };
        const serviceDetails = {
          name,
          description,
          serviceType,
          dataModelId,
          flow,
        };
        dispatch(setNewService(serviceDetails));
        dispatch(createService(serviceDetails)).then(({ payload }) => {
          if (payload) {
            handleClose();
            dispatch(setServiceFilters());
          }
        });
      }
    },
    [allServices, dispatch, dataModelId, handleClose, selectedServiceNodes]
  );

  const validationSchema = Yup.object({
    name: Yup.string()
      .matches(
        SPECIAL_CHARACTERS_NUMBERS_WHITESPACES_AT_START,
        NAME_SHOULD_CONTAIN
      )
      .matches(
        SPECIAL_CHARACTERS,
        NAME_SHOULD_NOT_CONTAIN
      )
      .min(3, MIN_CHAR_LENGTH)
      .max(21, MAX_CHAR_LENGTH_TWENTY_ONE)
      .matches(
        SPECIAL_CHARACTERS_WHITESPACES_AT_END,
        NAME_SHOULD_NOT_CONTAIN_AT_END
      )
      .required(SERVICE_REQUIRED)
      .test(CHECK_EXSISTANCE, SERVICE_EXISTS, (name) => name && !servicesNames.includes(name.toLowerCase())),
    description: Yup.string().matches(
      WHITESPACES_AT_START,
      DESCRIPTION_SHOULD_NOT_CONTAIN
    ),
    serviceType: Yup.string().required(),
  });

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleOnSubmit}
      validateOnMount={true}
    >
      {({
        values,
        isValid,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleReset,
      }) => {
        const footer = (
          <div className="p-6 border-t border-gray-700 flex items-center justify-end">
            <button
              className="btn btn-primary-light mr-1"
              type="button"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              type={SUBMIT}
              disabled={isSpinnerActive || !isValid}
            >
              <Spinner
                name={CREATE_SERVICE}
                setIsSpinnerActive={setIsSpinnerActive}
                persistSize
              >
                {SUBMIT}
              </Spinner>
            </button>
          </div>
        );
        return (
          <Form>
            <Modal
              title="Manage Service"
              handleReset={handleReset}
              handleClose={handleClose}
              footer={footer}
            >
              <div className="scrollbar flex-1 overflow-auto">
                <div className="space-y-4 p-6 border-b border-gray-700">
                  <div className="form-group">
                    <label className="text-sm font-medium text-gray-50 mb-1 block capitalize">
                      Service Name <span className="text-red-600 text-m mt-2">*</span>
                    </label>
                    <Field
                      type="text"
                      name="name"
                      className={classNames("form-control border-2", {
                        "border-red-600": touched.name && errors.name,
                      })}
                      placeholder="Service Name"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      autoFocus
                    />
                    {touched.name && errors.name && (
                      <div className="text-sm font-medium text-red-500">
                        {errors.name}
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="text-sm font-medium text-gray-50 mb-1 block capitalize">
                      Description
                    </label>
                    <Field
                      as="textarea"
                      name="description"
                      rows={1}
                      className={classNames("form-control border-2", {
                        "border-red-600":
                          touched.description && errors.description,
                      })}
                      placeholder="Description"
                      value={values.description}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {touched.description && errors.description && (
                      <div className="text-sm font-medium text-red-500">
                        {errors.description}
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="text-sm font-medium text-gray-50 mb-1 block">
                      Service Type <span className="text-red-600 text-m mt-2">*</span>
                    </label>
                    <Field
                      as="select"
                      name="serviceType"
                      className="form-control cursor-pointer"
                      value={values.serviceType}
                      onChange={handleChange}
                    >
                      {serviceTypeOptions.map(({ name, value }) => (
                        <option key={value} value={value}>
                          {name}
                        </option>
                      ))}
                    </Field>
                  </div>
                </div>
              </div>
            </Modal>
          </Form>
        );
      }}
    </Formik>
  );
};

export default ManageServiceModal;
